import apiTranslations from './pl.api.ts';
import primeVueTranslations from './pl.primevue.ts';
import {
  generalTerm,
  generalTermFilter,
} from './pl.shared.ts';

import npm from '@/generated/npm/locales/pl.npm.ts';
import {
  ERouteName,
} from '@/router/route-name.ts';
import {
  EAttributeLabel,
} from '@/types/attribute-type.ts';
import {
  EWeekday,
} from '@/types/calendar.ts';
import {
  EMonth,
} from '@/types/date.ts';
import {
  ETimeseriesMeaning,
} from '@/types/openapi-type.ts';
import {
  EVisualisation,
} from '@/types/performance-dashboard.ts';
import {
  TUnitSystem,
} from '@/types/unit-of-measurement.ts';
import {
  getComponentScopedTranslationMap,
} from '@/utils/i18n.ts';

const Visualisation: Record<EVisualisation, string> = {
  [EVisualisation.CLUSTERED_STACKED_BAR_CHART]: 'Wykres słupkowy klastrowany',
  [EVisualisation.PARETO_CHART]: 'Wykres Pareto',
  [EVisualisation.STACKED_BAR_CHART]: 'Wykres słupkowy skumulowany',
  [EVisualisation.STACKED_POLAR_CHART]: 'Składowany wykres biegunowy',
  [EVisualisation.TABLE]: 'Tabela',
};

const unitSystem: Record<TUnitSystem, string> = {
  metric: 'System metryczny',
  imperial: 'System imperialny',
};

export default {
  generalTermFilter,
  generalTerm,
  api: apiTranslations,
  primeVue: primeVueTranslations,
  npm,
  actions: {
    clear: 'Wyczyść',
    accept: 'Zaakceptuj',
    login: 'Login',
    reset: 'Zresetuj',
    search: 'Szukaj',
    select: 'Wybierz',
    unlink: 'Odłącz',
  },
  alerts: {
    saveFailed: 'Zapisywanie nie powiodło się',
    deleteSuccess: 'Pomyślnie usunięto',
    valueAboveMax: 'Wartość powinna być poniżej',
  },

  assetManagement: {
    confirmResurrection: {
      header: 'Przywrócić "{item}" do działania?',
      message: 'Wycofany zasób wróci do eksploatacji.',
    },
    confirmDelete: {
      header: 'Usunąć lub wycofać "{item}"?',
      message: 'Zasoby z danymi, także w zasobach podrzędnych, zostaną zamiast tego wycofane i można je później przywrócić.',
    },
    includeRetiredAssets: 'Pokaż emerytowanych',
    resurrectAsset: 'Przywróć do działania',
  },
  buttons: {
    acknowledge: 'Zatwierdź',
    analogInputs: 'Wejścia analogowe',
    cancel: 'Anuluj',
    digitalInputs: 'Wejścia cyfrowe',
    editOverview: 'Edytuj przegląd',
    loadList: 'Załaduj listę',
    upload: 'Prześlij',
  },
  calendar: {
    occurs: 'Występuje',
    on: 'NA',
    startingOn: 'zaczynając od',
  },
  charts: {
    position: {
      left: 'Lewo',
      right: 'Prawo',
    },
    newAxis: 'Nowa oś',
    noAlarms: 'Brak alarmów',
  },
  fullscreen: {
    exitFullscreen: 'Wyjdź z trybu pełnoekranowego',
    fullscreen: 'Pełny ekran',
  },
  measurementUnit: {
    noGroup: 'Inny',
  },
  attributeUnit: {
    [EAttributeLabel.WEIGHT]: 'Waga',
    [EAttributeLabel.LENGTH]: 'Długość',
    [EAttributeLabel.DIAMETER]: 'Średnica',
    [EAttributeLabel.SURFACE_HARDNESS]: 'Twardość powierzchni',
    [EAttributeLabel.SURFACE_HARDNESS_MIN]: 'Minimalna twardość powierzchni',
    [EAttributeLabel.SURFACE_HARDNESS_MAX]: 'Maksymalna twardość powierzchni',
    [EAttributeLabel.CORE_HARDNESS]: 'Twardość rdzenia',
    [EAttributeLabel.CORE_HARDNESS_MIN]: 'Minimalna twardość rdzenia',
    [EAttributeLabel.CORE_HARDNESS_MAX]: 'Maksymalna twardość rdzenia',
    [EAttributeLabel.EFFECTIVE_CASE_DEPTH_MIN]: 'ECD min',
    [EAttributeLabel.EFFECTIVE_CASE_DEPTH_MAX]: 'ECD max',
    [EAttributeLabel.EFFECTIVE_CASE_DEPTH_HARDNESS]: 'Twardość ECD',
    neuAttributePlaceholder: 'Etykieta atrybutu',
  },
  equipmentGasName: {
    [ETimeseriesMeaning.ACETYLENE_C2H2_FLOW_SETPOINT]: 'Acetylen',
    [ETimeseriesMeaning.AIR_FLOW_SETPOINT]: 'Powietrze',
    [ETimeseriesMeaning.AMMONIA_NH3_FLOW_SETPOINT]: 'Amoniak',
    [ETimeseriesMeaning.ARGON_FLOW_SETPOINT]: 'Argon',
    [ETimeseriesMeaning.CARBON_DIOXIDE_CO2_FLOW_SETPOINT]: 'CO2',
    [ETimeseriesMeaning.CARBON_MONOXIDE_CO_FLOW_SETPOINT]: 'CO',
    [ETimeseriesMeaning.DISSOCIATED_AMMONIA_NH3_FLOW_SETPOINT]: 'Zdysocjowany amoniak',
    [ETimeseriesMeaning.ENDO_GAS_NATURAL_FLOW_SETPOINT]: 'Endo Gas Natural',
    [ETimeseriesMeaning.ENDO_GAS_PROPANE_FLOW_SETPOINT]: 'Endo Gas Propan',
    [ETimeseriesMeaning.ETHENE_C2H4_FLOW_SETPOINT]: 'Etena',
    [ETimeseriesMeaning.HELIUM_FLOW_SETPOINT]: 'Hel',
    [ETimeseriesMeaning.HYDROGEN_H2_FLOW_SETPOINT]: 'Wodór',
    [ETimeseriesMeaning.METHANOL_CH3OH_FLOW_SETPOINT]: 'Metanol',
    [ETimeseriesMeaning.NATURAL_GAS_FLOW_SETPOINT]: 'Gaz ziemny',
    [ETimeseriesMeaning.NITROGEN_FLOW_SETPOINT]: 'Azot',
    [ETimeseriesMeaning.PROPANE_C3H8_FLOW_SETPOINT]: 'Propan',
    [ETimeseriesMeaning.WATER_FLOW_SETPOINT]: 'Woda',
    [ETimeseriesMeaning.NITROUS_OXIDE_N2O_FLOW_SETPOINT]: 'N2O',
    [ETimeseriesMeaning.METHANE_CH4_FLOW_SETPOINT]: 'Metan',
    [ETimeseriesMeaning.OXYGEN_O2_FLOW_SETPOINT]: 'Tlen',
  },
  // single words or small phrases WITHOUT CONTEXT
  common: {
    min: 'Minimum',
    max: 'Maksimum',
    emptyTable: 'Nie znaleziono pasujących danych.',
    attribute: 'Atrybut',
    revealItem: 'Pokaż {item}',
    hideItem: 'Ukryj {item}',
    numberedItem: '{item} {number}',
    valueRange: 'Zakres wartości',
    all: 'Wszystkie',
    user: 'Użytkownik',
    date: 'Data',
    actions: 'Akcje',
    yes: 'Tak',
    no: 'Nie',
    clear: 'Wyczyść',
    delete: 'Usuń',
    discard: 'Odrzuć',
    keepEditing: 'Kontynuuj edycję',
    save: 'Zapisz',
    duration: 'Czas trwania',
    download: 'Pobierz {item}',
    comment: 'Komentarz',
    print: 'Drukuj',
    chart: 'Wykres',
    description: generalTerm.description,
    status: 'Status',
    assignee: 'Przypisany',
    temperature: 'Temperatura',
    name: 'Nazwa',
    filters: 'Filtry',
    overview: 'Przegląd',
    type: 'Typ',
    set: 'Ustaw',
    username: 'Nazwa użytkownika',
    password: 'Hasło',
    include: 'Uwzględnij',
    exclude: 'Wyklucz',
    custom: 'Zwyczaj',
    loading: 'Załadunek',
    retired: 'emerytowany',
    carbon: 'Węgiel',
    enterForNewRow: 'Naciśnij "Enter", aby Dodaj nowy wiersz',
    now: 'Teraz',
    note: 'Notatka',
    target: 'Cel',
    source: 'Źródło',
    skip: 'Pomiń',
    reject: 'Odrzuć',
    tag: 'Tag | Tagi',
    withoutTag: 'Bez tagu',
    maintenance: 'Konserwacja',
    filter: 'Filtr | Filtry',
    navigation: 'Nawigacja',
    calendar: 'Kalendarz',
    noAccessToAction: 'Nie masz dostępu do tej akcji.',
    tiny: 'Mały',
    large: 'Duży',
    size: 'Rozmiar',
    appearance: 'Wygląd',
    notification: 'Powiadomienie | Powiadomienia',
    meaning: 'Znaczenie| Znaczenia',
    result: 'Wynik',
    toggle: 'Dźwignia kolankowa',
    statistics: 'Statystyka',
    rating: 'Ocena',
    showTable: 'Pokaż tabelę',
    showChart: 'Pokaż wykres',
    configuration: 'Konfiguracja',
    passed: 'Przeszedł',
    failed: 'Przegrany',
    section: 'Sekcja',
    capacity: 'pojemność | Pojemności',
    gas: 'Gaz | Gazy',
    capability: 'Możliwości | Możliwości',
    certificate: 'Certyfikat | Certyfikaty',
    model: 'Model | Modele',
    createItem: 'Utwórz {item}',
    legend: 'Legenda',
    linkItem: 'Link {item}',
    today: 'Dzisiaj',
    earlier: 'Wcześniej',
    addItem: 'Dodaj {item}',
    image: 'Obraz',
    file: 'Plik',
    pasteFromClipboard: 'Wklej ze schowka',
    noItemInClipboard: 'Brak {item} w schowku',
    assetNavigation: 'Nawigacja po zasobach',
    noRecipeSelected: 'Nie wybrano receptury',
    addAssessment: 'Dodaj ocenę',
    addRow: 'Dodaj wiersz',
    setAll: 'Ustaw wszystko',
    resetAll: 'Zresetuj wszystko',
    unlock: 'Odblokuj',
    notAvailable: 'N/D',
    stop: 'Zatrzymaj',
    loadItem: 'Załaduj {item}',
    add: 'Dodaj',
    valueAtTime: '{value} w {time}',
    timeline: 'Oś czasu',
    Ferrite: 'Ferryt',
    Austenite: 'Austenit',
    Martensite: 'Martenzyt',
    labelInUnit: '{label} w {unit}',
    default: 'Domyślny',
    settings: 'Ustawienia',
    version: 'Wersja',
    logout: 'Wyloguj',
    confirm: 'Potwierdź',
    edit: 'Edytuj',
    start: 'Start',
    confirmUnlink: {
      header: 'Odłączyć "{item}"?',
      message: 'Oba wpisy zostaną zachowane i można je ponownie połączyć.',
    },
    confirmDelete: {
      header: 'Usunąć "{item}"?',
      message: 'Tej operacji nie można cofnąć.',
    },
    confirmRemove: {
      header: 'Usunąć "{item}"?',
    },
    takesEffectOnSave: 'Zmiana zostanie zastosowana po zapisaniu.',
    confirmSaveChanges: {
      header: 'Zapisać zmiany w "{item}"?',
      message: 'Zapisana wersja zostanie nadpisana.',
    },
    confirmChangeValue: {
      header: 'Zmienić wartość na "{value}"?',
      message: 'Aktualna wartość to "{currentValue}".',
    },
    remove: 'Usuń',
    selectAll: 'Zaznacz wszystko',
    selectItem: 'Wybierz {item}',
    deselectAll: 'Odznacz wszystko',
    value: 'Wartość | Wartości',
    validate: 'Zwaliduj',
    selectEquipment: 'Aby zobaczyć szczegóły, wybierz zasób z nawigacji zasobów po lewej stronie',
    required: 'Wymagany',
    new: 'Nowy',
    calendarWeekAbbreviation: 'CW',
    unlink: 'Odłącz',
    other: 'Inny',
    cancel: 'Anuluj',
    error: 'Błąd',
    schedule: 'Harmonogram',
    always: 'Zawsze',
    asset: 'Zasób',
    assets: 'Zasoby',
    severityLevels: 'Poziomy ważności',
    history: 'Historia',
    restore: 'Przywróć',
    errorMessage: {
      errorSignalLoading: 'Błąd ładowania {displayName}',
      noAccess: '{entity} nie ma dostępu "{accessType}" do {item}',
      default: 'Coś poszło nie tak, proszę odświeżyć stronę. Jeśli błąd będzie się powtarzał, prosimy o kontakt z pomocą techniczną.',
      pt9800: {
        AxiosError401: 'Mapowanie użytkowników nie zostało wykonane lub jest nieprawidłowe',
        AxiosError401Item: 'Mapowanie użytkownika nie zostało wykonane lub jest nieprawidłowe dla {item}',
        AxiosError403: 'Użytkownik nie posiada wymaganych uprawnień do dostępu do danych Protherm',
        AxiosError403Item: 'Użytkownik nie ma wymaganych uprawnień do dostępu do danych Protherm dla {item}',
      },
    },
    fileGeneration: {
      summary: 'Generowanie pliku',
      detail: 'Generowanie pliku może chwilę potrwać. Proszę pozostawić otwarte okno przeglądarki. Ten komunikat zostanie zamknięty po zakończeniu żądania',
    },
    update: 'Aktualizacja',
    create: 'Utwórz',
    createCopy: 'Utwórz kopię',
    deleteItem: 'Usuń {item}',
    editItem: 'Edytuj {item}',
    saveItem: 'Zapisz {item}',
    saveAsNew: 'Zapisz jako...',
    export: 'Eksport',
    import: 'Import',
    search: 'Szukaj',
    selected: 'Wybrany',
    back: 'Z powrotem',
    forward: 'Do przodu',
    accept: 'Zaakceptuj',
    submit: 'Potwierdź',
    success: 'Sukces',
    close: 'Zamknij',
    week: {
      [EWeekday.MONDAY]: apiTranslations.Weekday.MONDAY,
      [EWeekday.TUESDAY]: apiTranslations.Weekday.TUESDAY,
      [EWeekday.WEDNESDAY]: apiTranslations.Weekday.WEDNESDAY,
      [EWeekday.THURSDAY]: apiTranslations.Weekday.THURSDAY,
      [EWeekday.FRIDAY]: apiTranslations.Weekday.FRIDAY,
      [EWeekday.SATURDAY]: apiTranslations.Weekday.SATURDAY,
      [EWeekday.SUNDAY]: apiTranslations.Weekday.SUNDAY,
    },
    month: {
      [EMonth.JANUARY]: {
        long: 'Styczeń',
        short: 'Styczeń',
      },
      [EMonth.FEBRUARY]: {
        long: 'Luty',
        short: 'Luty',
      },
      [EMonth.MARCH]: {
        long: 'Marzec',
        short: 'Mar',
      },
      [EMonth.APRIL]: {
        long: 'Kwiecień',
        short: 'Kwiecień',
      },
      [EMonth.MAY]: {
        long: 'Maj',
        short: 'Maj',
      },
      [EMonth.JUNE]: {
        long: 'Czerwiec',
        short: 'Czerwiec',
      },
      [EMonth.JULY]: {
        long: 'Lipiec',
        short: 'Lipiec',
      },
      [EMonth.AUGUST]: {
        long: 'Sierpień',
        short: 'Sierpień',
      },
      [EMonth.SEPTEMBER]: {
        long: 'Wrzesień',
        short: 'Wrzesień',
      },
      [EMonth.OCTOBER]: {
        long: 'Październik',
        short: 'Październik',
      },
      [EMonth.NOVEMBER]: {
        long: 'Listopad',
        short: 'Listopad',
      },
      [EMonth.DECEMBER]: {
        long: 'Grudzień',
        short: 'Grudzień',
      },
    },
    timeAbbreviation: {
      days: 'dni',
      hours: 'godz.',
      minutes: 'min',
      months: 'M',
      weeks: 'tydzień',
      years: 'rok.',
    },
    reset: 'Zresetuj',
    none: 'Nic',
    copy: 'Kopia',
    copyNoun: 'Kopia',
    time: {
      second: 'Sekunda | Sekundy',
      minute: 'Minuta | Minuty',
      hour: 'Godzina | Godziny',
      day: 'Dzień | Dni',
      weekday: 'Dzień roboczy | Dni robocze',
      week: 'Tydzień | Tygodnie',
      month: 'Miesiąc | Miesiące',
      quarter: 'Kwartał | Kwartały',
      year: 'Rok | Lata',
    },
    task: 'Zadanie | Zadania',
    read: 'Odczyt',
    write: 'Zapis',
  },
  component: {
    accordionAudit: {
      envelopeNamePlaceholder: 'Nazwa granicy',
    },
    alarmView: {
      header: 'Alarmy',
      empty: 'Nie znaleziono alarmów',
    },
    appAndAssetPermissionTree: {
      appPermission: 'Uprawnienia aplikacji',
      assetPermission: 'Uprawnienia zasobów',
    },
    counterActionModal: {
      create: 'Utwórz działanie zaradcze',
      update: 'Aktualizuj działanie zaradcze',
    },
    counterActionTable: {
      create: 'Utwórz działanie zaradcze',
      confirmDelete: 'Usunąć działanie zaradcze "{counterActionName}"?',
      confirmUnlink: 'Odłączyć przyczynę "{rootCauseName}" od działania zaradczego "{counterActionName}"?',
    },
    dashboardPanel: {
      add: 'Dodaj panel',
      defaultTitle: 'Mój panel',
    },
    furnaceViewRecipe: {
      stopConfirm: {
        header: 'Zatrzymać recepturę "{0}"?',
        message: 'Receptura zostanie natychmiast zatrzymana.',
      },
      startConfirm: {
        header: 'Uruchomić recepturę "{0}"?',
        message: 'Receptura zostanie natychmiast uruchomiona.',
      },
      goToConfirm: {
        message: 'Receptura będzie natychmiast kontynuowana od tego kroku.',
      },
    },
    expressionPreviewModal: {
      title: 'Nowy wirtualny sygnał',
    },
    equipmentDocumentUploadModal: {
      headline: 'Prześlij dokumenty',
      categoryTitle: 'Kategoria',
      descriptionTitle: 'Opis',
      uploadFilesTitle: 'Plik',
      errorMessage: 'Plik już istnieje',
    },
    loadModal: {
      startingLoad: 'Job jest uruchamiany...',
      confirmPartHint: 'Potwierdź, że wszystkie części zostały załadowane',
      confirmStart: 'Potwierdź uruchomienie joba.',
      loadIdentifier: generalTerm.loadIdentifier,
      createOrUpdate: 'Zaplanuj job',
      description: 'Zaplanuj następne załadowanie, dodając swoje',
      addTraveller: 'Dodaj nowego towarzysza',
      emptyPartsTable: 'Nie dodano żadnych części',
      partNotFound: 'Część o ID "{id}" nie została znaleziona',
      partDoNotMatch: 'Nie wszystkie części pasują do receptury',
      readyToScan: 'Gotowy do skanowania',
    },
    materialSelect: {
      placeholder: 'Wybierz materiał',
    },
    materialGroupTable: {
      tableHeader: 'Grupy materiałów',
    },
    maintenanceCalendarJobModal: {
      titleCreate: 'Utwórz zlecenie konserwacji',
      titleEdit: 'Edytuj zlecenie konserwacji',
      createTask: 'Utwórz zadanie',
    },
    observationView: {
      header: 'Obserwacje',
    },
    observationTypeTable: {
      unlinkConfirm: 'Odłączyć przyczynę "{rootCauseName}" od typu obserwacji "{observationTypeName}"?',
    },
    PartAttribute: {
      headline: 'Ustawienia części',
      subheadline: 'Atrybuty zdefiniowane w tej tabeli będą wymagane do wypełnienia podczas tworzenia/modyfikacji części.',
    },
    productionLogsTable: {
      tab: {
        table: 'Tabela',
        chart: 'Wykres',
      },
    },
    qualityForm: {
      jobSelection: 'Wybór wsadu',
      partSelection: 'Wybór części',
    },
    rootCauseLinkModal: {
      title: 'Powiąż przyczynę',
    },
    rootCauseModal: {
      create: 'Utwórz przyczynę',
      update: 'Aktualizuj przyczynę',
    },
    rootCauseGroupModal: {
      create: 'Utwórz grupę przyczyn',
      update: 'Aktualizuj grupę przyczyn',
    },
    rootCauseTable: {
      create: 'Utwórz przyczynę',
      confirmDelete: 'Usunąć przyczynę "{rootCauseName}"?',
      confirmUnlink: 'Odłączyć działanie zaradcze "{counterActionName}" od przyczyny "{rootCauseName}"?',
    },
    rootCauseGroupTable: {
      create: 'Utwórz grupę przyczyn',
      confirmDelete: 'Usunąć grupę przyczyn "{rootCauseGroupName}"?',
      confirmUnlink: 'Odłączyć przyczynę "{rootCauseName}" od grupy przyczyn "{rootCauseGroupName}"?',
    },
    TableJobStages: {
      header: 'Etapy receptury',
    },
    treeSidebarTemplate: {
      defaultTitle: 'Nawigacja zasobów',
    },
    userUpdateModal: {
      tab: {
        permissionList: 'Uprawnienia',
        controllerUserMapping: 'Mapowanie użytkownika kontrolera',
      },
      permissionList: 'Lista uprawnień',
      groupList: 'Lista grup',
      selectPT9800: 'Wybierz użytkownika Protherm',
      enterCredentials: 'Wprowadź dane uwierzytelniające',
    },
  },
  details: {
    images: 'Obrazy',
  },
  errorMessages: {
    error: 'Błąd',
    noEquipmentSelected: 'Aby zobaczyć szczegóły, wybierz zasób z nawigacji zasobów po lewej stronie',
    outOfRangeTemperature: 'Temperatura jest poza zakresem',
  },
  forms: {
    credentials: 'Referencje',
    persistCredentials: 'Trwałe dane uwierzytelniające',
  },
  global: {
    workplace: 'Miejsce pracy',
  },
  images: {
    noImages: 'Brak obrazów',
  },
  inputLabels: {
    from: 'z',
    to: 'Do',
  },
  materialComposition: {
    min: {
      label: 'Min',
      description: 'Analiza z najmniejszymi właściwościami materiału.',
    },
    max: {
      label: 'Maks',
      description: 'Analiza z największymi właściwościami materiału.',
    },
    avg: {
      label: 'Średnia',
      description: 'Analiza ze średnimi właściwościami materiału.',
    },
    comp: {
      label: 'Analiza średnia',
      description: 'Analiza średnia na podstawie procentów wagowych.',
    },
  },
  modifyMenu: {
    addNew: 'Nowy {0}',
    edit: 'Edytuj',
    saveAs: 'Zapisz jako',
  },
  page: {
    anomalyDetection: {
      noObservations: 'Nie znaleziono obserwacji',
    },
    certification: {
      title: 'Audyt i jakość',
      auditConfiguration: {
        deleteSuccess: 'Specyfikacja została pomyślnie usunięta.',
      },
    },
    openSourceLicenses: {
      title: 'Licencje open source',
    },
    liveApp: {
      equipment: {
        noJobs: 'Nie znaleziono zadań',
      },
    },
    notification: {
      title: 'Powiadomienia',
    },
    userManagement: {
      manageRole: 'Zarządzaj rolami',
      headline: 'Użytkownicy ({0})',
      customPermissions: 'Uprawnienia niestandardowe',
      searchPlaceholder: {
        name: 'Szukaj po nazwie',
        email: 'Szukaj według e-maila',
        role: 'Szukaj według roli',
      },
      showDeletedUsers: 'Pokaż usuniętych użytkowników',
      restoreUser: 'Przywróć użytkownika',
      confirmRestoreUser: {
        header: 'Przywrócić "{displayName}"?',
        message: 'Użytkownik będzie mógł ponownie zalogować się do Qmulus.',
      },
      confirmDeleteUserMessage: 'Usuniętych użytkowników można przywrócić.',
      deleted: 'Usunięty',
    },
    roleManagement: {
      roles: 'Role',
      createRole: 'Utwórz rolę',
    },
    home: {
      title: 'Strona główna',
      welcomeMorning: 'Dzień dobry, {firstName}.',
      welcomeAfternoon: 'Dobry dzień, {firstName}.',
      welcomeEvening: 'Dobry wieczór, {firstName}.',
      welcomeNight: 'Jeszcze w pracy o tej porze, {firstName}?',
      welcomeSubtitles: [
        'Twoje piece czekają — sprawmy, żeby dzisiejszy wsad był doskonały.',
        'Czas rozgrzać dzień — Twoje procesy są gotowe.',
        'Zadbajmy o to, żeby każdy zlecenie dziś przebiegło zgodnie ze specyfikacją.',
        'Monitoruj swoje lokalizacje, śledź zlecenia i zachowaj kontrolę.',
        'Wszystkie Twoje operacje obróbki cieplnej zawsze pod ręką.',
        'Twój przegląd każdej lokalizacji, każdego pieca, każdego zlecenia.',
        'Co dziś pracuje? Przejdź do przeglądu aplikacji i sprawdź.',
        'Miej swoje wsady pod kontrolą i dbaj o jakość.',
        'Sprawdź swoje piece i utrzymuj procesy na właściwym kursie.',
      ],
    },
    appOverview: {
      title: 'Przegląd aplikacji',
      showOnlyAvailableApps: 'Pokaż tylko dostępne aplikacje',
      search: 'Szukaj aplikacji',
    },
    releaseNotes: {
      title: 'Notatki o wydaniu',
    },
    releaseHighlights: {
      title: 'Nowości',
    },
    userManual: {
      title: 'Instrukcja obsługi',
    },
    productionStatisticsDetails: {
      panelSettings: {
        header: 'Ustawienia panelu',
        helpText: 'Wybierz tytuł panelu i wybierz wskaźniki KPI, które mają być wyświetlane. Typ wykresu można wybrać na panelu.',
        labelTitle: 'Tytuł',
        labelSize: 'Rozmiar',
        labelType: 'Typ',
        labelKpiSelection: 'Wybór KPI',
      },
      size: {
        medium: 'Średni',
        large: 'Duży',
      },
      type: {
        table: 'Tabela',
        totals: 'Razem',
        totalDistribution: 'Całkowita dystrybucja',
        childrenTotals: 'Liczba dzieci',
        childrenDistribution: 'Dystrybucja dzieci',
        childrenComparison: 'Porównanie dzieci',
      },
    },
    assetManagementEquipmentSensorMapping: {
      mappedSignalTitle: 'Zmapowany sygnał',
    },
    assetManagement: {
      overview: {
        title: 'Przegląd zasobów',
        tableHeader: 'Drzewo zasobów',
        filter: {
          name: 'Filtruj według nazwy',
        },
      },
      equipmentTab: {
        [ERouteName.ASSET_MANAGEMENT_ALARM_MANAGEMENT]: 'Zarządzanie alarmami',
        [ERouteName.ASSET_MANAGEMENT_CAMERAS]: 'Kamery',
        [ERouteName.ASSET_MANAGEMENT_DATA_SOURCES]: 'Źródła danych',
        [ERouteName.ASSET_MANAGEMENT_DOCUMENT]: 'Dokumenty',
        [ERouteName.ASSET_MANAGEMENT_MAIN]: 'Przegląd',
        [ERouteName.ASSET_MANAGEMENT_PARAMETER]: 'Parametry',
        [ERouteName.ASSET_MANAGEMENT_SENSOR_MAPPING]: 'Mapowanie czujników',
        [ERouteName.ASSET_MANAGEMENT_SENSOR_SETTINGS]: 'Ustawienia czujników',
      },
    },
    pt9800: {
      common: {
        id: 'ID',
        name: 'Nazwa',
        description: 'Opis',
        modifiedBy: 'Zmodyfikowane przez',
        modifiedOn: 'Zmodyfikowano dnia',
        number: 'Numer',
        comment: 'Komentarz',
      },
      confirmDeleteHeader: 'Usunąć {0} "{1}"?',
      error: {
        create: 'Nie udało się utworzyć {0}',
        update: 'Nie udało się zaktualizować {0}',
        delete: 'Nie udało się Usunąć {0}',
        clone: 'Nie udało się sklonować {0}',
      },
      form: {
        header: {
          create: 'Utwórz {0}',
          edit: 'Edytuj {0}',
          delete: 'Usuń {0}',
        },
      },
      entryType: {
        alarm: 'Alarm',
        application: 'Aplikacja',
        applicationGroup: 'Grupa aplikacji',
        box: 'Skrzynka',
        clientAccess: 'Dostęp klienta',
        color: 'Kolor',
        terminal: 'Terminal',
        terminalType: 'Typ terminala',
        user: 'Użytkownik',
        userAccess: 'Dostęp użytkownika',
        userGroup: 'Grupa użytkowników',
        backgroundProcess: 'Proces w tle',
        tasks: 'Zadania',
        workplace: 'Miejsce pracy',
        workplaceType: 'Typ miejsca pracy',
        operation: 'Działanie',
      },
      backgroundProcess: {
        order: 'Zamówienie',
        parameter: 'Parametr',
        active: 'Aktywny',
        activeTime: 'Czas aktywny',
        pid: 'PID',
        header: 'Proces w tle',
      },
      operation: {
        header: 'Działanie',
        property: 'Nieruchomość',
        operationType: 'Typ operacji',
        type: 'Typ',
        mode: 'Tryb',
        order: 'Zamówienie',
        peProcessType: 'Typ procesu PE',
        sortingNo: 'Sortowanie nie',
        automatingPlanningMode: 'Tryb automatycznego planowania',
        relevantForProductionStatistics: 'Istotne dla statystyk produkcyjnych',
        relevantForStat: 'Istotne dla statystyki',
        selectProcess: 'Wybierz proces',
        selectPart: 'Wybierz część',
        sapNo: 'Sap nr.',
        workplaceName: 'Miejsce pracy/Grupy robocze',
        placeDescription: 'Opis miejsca',
      },
      tasks: {
        header: 'Administracja zadaniami',
        active: 'Aktywny',
        type: 'Typ',
        command1: 'Polecenie 1',
        command2: 'Dowództwo 2',
        command3: 'Dowództwo 3',
        command4: 'Dowództwo 4',
        command5: 'Dowództwo 5',
        command6: 'Dowództwo 6',
        command7: 'Dowództwo 7',
        command8: 'Dowództwo 8',
        command9: 'Dowództwo 9',
        command10: 'Polecenie 10',
        startHour: 'Godzina rozpoczęcia',
        dateLastActive: 'Data ostatniej aktywności',
        intervalInMin: 'Interwał w min [min]',
      },
      workplace: {
        logicTypes: 'Typy logiki',
        header: 'Miejsce pracy',
        inventoryNumber: 'Numer sprzętu',
        belongsTo: 'Należy do identyfikatora miejsca pracy',
        ip: 'adres IP',
        mGuardIp: 'MGuard IP',
        device: 'Urządzenie',
        telegramSplit: 'Podział telegramu RFC',
        deactivateOperations: 'Dezaktywuj operacje',
        aliveBitErrorNum: 'Numer błędu bitu aktywnego',
        logicType: 'Typ logiczny',
        recipePrefix: 'Prefiks receptury',
        chamberId: 'Identyfikator komnaty',
        sharesController: 'Kontroler akcji/kopia identyfikatora izby',
        mainUseMode: 'Główny tryb użytkowania',
        workplaceType: 'Typ miejsca pracy',
        isActive: 'Jest aktywny',
        importantSettings: 'Ważne ustawienia',
      },
      alarm: {
        alarmAdministration: 'Alarm',
        alarm: 'Alarm',
        alarmText: 'Tekst alarmu',
        causesTestingLoad: 'Powoduje obciążenie testowe',
        onlyForChamber: 'Tylko dla izby',
        security: 'Bezpieczeństwo',
        color: 'Kolor',
        errorType: 'Typ błędu',
        outputType: 'Typ wyjścia',
        alarmGroup: 'Grupa alarmowa',
      },
      application: {
        header: 'Aplikacja',
        command: 'Rozkaz',
        groupId: 'Grupa',
        iconGifName: 'Ikona-Nazwa-Gifu',
        furnacePicture: 'Obraz pieca',
        order: 'Zamówienie',
        parameter: 'Parametr',
        tooltip: 'Podpowiedź',
        typeId: 'Typ',
      },
      applicationGroup: {
        header: 'Grupa aplikacji',
      },
      systemData: {
        active: 'Aktywny',
        addresseeMobilePhoneNo: 'Numer telefonu komórkowego adresata',
        boxHeader: 'Administracja pudełek',
        colorHeader: 'Administracja kolorów',
        colorValue: 'Wartość koloru',
        pin: 'SZPILKA',
        serialInterface: 'Interfejs szeregowy',
        smsHeader: 'SMS-y',
        testMessage: 'Wiadomość testowa (1=TAK)',
        used: 'Używany',
      },
      terminal: {
        header: 'Terminal',
        id: 'Id',
        clientType: 'Typ terminala',
        name: 'Nazwa',
        osType: 'System operacyjny',
        remark: 'Komentarz',
        address: 'Adres',
        description: 'Opis',
        location: 'Lokalizacja',
        valueLong2: 'Wartość INT 2',
        valueLong3: 'Wartość INT 3',
        valueLong4: 'Wartość INT 4',
        valueLong5: 'Wartość INT 5',
        valueChar1: 'Wartość znaku 1',
        valueChar2: 'Wartość znaku 2',
        actUserName: 'Aktualny użytkownik',
        actLoginTime: 'Zalogowany od',
        terminalNo: 'Numer terminala',
        lastLoginTime: 'Ostatni czas logowania',
        lastUserName: 'Ostatni użytkownik',
        resolution: 'Rozdzielczość ekranu',
        permission: 'Pozwolenie',
        rightsToTerminals: 'Prawa do terminali',
      },
      terminalType: {
        header: 'Typ terminala',
      },
      user: {
        header: 'Użytkownik',
        accessLevel: 'Poziom dostępu użytkownika',
        comment: 'Komentarz',
        costCenter: 'Centrum kosztów',
        employeeNumber: 'Numer pracownika',
        shortName: 'Krótka nazwa',
        firstName: 'Imię',
        function: 'Funkcja',
        lastName: 'Nazwisko',
        language: 'Język',
        loginTimeInMin: 'Czas logowania w minutach.',
        maintenancePopups: 'Okna planu konserwacji',
        password: 'Hasło',
        plant: 'Zakład',
        playAlarmSound: 'Odtwórz dźwięk alarmu w alarmach',
        userGroup: 'Grupa użytkowników',
      },
      userAccess: {
        header: 'Dostęp użytkownika',
        delete: 'Usuń',
        newEntry: 'Nowy wpis',
        print: 'Wydrukuj',
        run: 'Uruchom',
        save: 'Zapisz',
        special1: 'Specjalny-1',
        startRecipeProcessFlow: 'Rozpocznij recepturę/przepływ procesu',
        modifyRecipeProcessFlow: 'Modyfikuj recepturę/przepływ procesu',
        startCorrectionRecipe: 'Rozpocznij recepturę korekcji',
        stopRecipeProcessFlow: 'Zatrzymaj recepturę/przepływ procesu',
        selectUser: 'Wybierz użytkownika',
      },
      userGroup: {
        header: 'Grupa użytkowników',
      },
      workplaceType: {
        header: 'Typ miejsca pracy',
        prio: 'Prio',
      },
    },
    login: {
      title: 'Login',
    },
    anomalyDetectionJobFeatureDetail: {
      tab: {
        observationTable: 'Obserwacje',
        rootCauseTable: 'Przyczyny źródłowe',
      },
    },
    anomalyDetectionEquipmentSettings: {
      title: 'Ustawienia',
      tab: {
        counterActionTable: 'Działania przeciwstawne',
        rootCauseGroupTable: 'Grupa ds. przyczyn źródłowych',
        rootCauseTable: 'Przyczyny źródłowe',
        jobFeatureTable: 'Cechy pracy',
      },
    },
    anomalyDetectionEquipment: {
      tab: {
        rootCauseTable: 'Możliwe przyczyny źródłowe',
        jobFeatureTable: 'Obserwacje pogrupowane według cech pracy',
      },
      jobFeatureTable: {
        header: {
          jobFeatureName: 'Cecha pracy',
          observationCount: 'Obserwacje się liczą',
          rootCauseCount: 'Liczba przyczyn źródłowych',
        },
      },
      observationTypeByJobFeatureTable: {
        header: {
          observationTypeName: 'Nazwa typu obserwacji',
          observationCount: 'Obserwacje się liczą',
          rootCauseIdList: 'Liczba przyczyn źródłowych',
        },
      },
    },
    quality: {
      header: {
        title: 'Jakość',
        createReport: 'Utwórz raport',
      },
    },
    auditResult: {
      asset: 'Zasób',
      userOverruledReason: 'Powód nadpisania przez użytkownika',
      duration: 'Czas trwania',
      endTime: 'Czas końcowy',
      headerTitle: 'Raport z audytu',
      loadNo: 'Załaduj nr',
      location: 'Lokalizacja',
      rating: 'Ocena',
      recipe: 'Receptura',
      reportNo: 'Numer raportu',
      reporter: 'Zgłaszający',
      signature: {
        customer: 'Klient',
        engineering: 'Inżynieria',
        manufacturing: 'Produkcja',
        material: 'Materiał',
        program: 'Program',
        quality: 'Jakość',
      },
      startTime: 'Czas rozpoczęcia',
    },
    productionLogList: {
      compare: 'Porównaj',
      export: 'Eksport',
    },
  },
  recipeBuilder: {
    alarmDeactivated: 'Alarm wyłączony',
    alarmDelay: 'Opóźnienie alarmu',
    equi: 'Równy',
    highAlarmOffsets: 'Wysokie przesunięcia alarmu',
    highWarningOffsets: 'WYSOKIE przesunięcia ostrzegawcze',
    lowAlarmOffsets: 'NISKIE przesunięcia alarmu',
    lowWarningOffsets: 'NISKIE przesunięcia ostrzegawcze',
    referenceValue: 'Wartość odniesienia',
    setAlarmLimit: 'Ustaw limit alarmu',
    setAlarmLimits: 'Ustaw limity alarmowe',
    setValue: 'Ustaw wartość',
  },
  recipeOnDemand: {
    chart: {
      gasCompositionLabel: {
        ammonia: 'Amoniak',
        dissociatedAmmonia: 'Zdysocjowany amoniak',
        nitrogen: 'Azot',
        carbonOxide: 'Dwutlenek węgla',
        carbonDioxide: 'Dwutlenek węgla',
      },
      processLabel: {
        knAct: 'Ustawa Kn',
      },
    },
    compoundLayerThickness: 'Grubość warstwy złożonej',
    coreHardnessHV: 'Twardość rdzenia (HV)',
    diffusionLayerThickness: 'Grubość warstwy dyfuzyjnej',
    distance: 'Dystans',
    effective: 'Skuteczny',
    gasFlowChart: 'Wykres przepływu gazu',
    hardness: 'Twardość',
    material: 'Materiał',
    nitridetHardness: 'Głębokość twardości azotowanej',
    nitriding: 'Azotowanie',
    nitrocarburizing: 'Azotonawęglanie',
    oxideLayerThickness: 'Grubość warstwy tlenku',
    porosity: 'Porowatość %',
    postoxidation: 'Postoksydacja',
    process: 'Proces',
    processChart: 'Wykres procesu',
    profileChart: 'Wykres profilu',
    stepNumber: 'Krok {number}',
    surfaceHardness: 'Twardość powierzchni',
    unit: 'Jednostka',
    unitSystems: {
      imperial: 'Imperialny',
      metric: 'Metryczny',
    },
    vickersHardness: 'Twardość Vickersa',
  },
  recipes: {
    carburizingDepth: generalTerm.carburizingDepth,
    endTemperature: 'Temperatura końcowa',
    grainSize: 'Numer wielkości ziarna ASTM',
    nitridingDepth: generalTerm.nitridingDepth,
    quenchingIntensity: 'Gaszenie intensywności',
    representativeDiameter: 'Średnica reprezentatywna',
    requiredHardnessHV: 'Wymagana twardość',
    noAccess: 'Użytkownik nie ma dostępu do {0}',
  },
  report: {
    approvals: 'Zatwierdzenia',
    customer: 'Klient',
    engineering: 'Inżynieria',
    manufacturing: 'Produkcja',
    materiel: 'Materiel',
    program: 'Program',
    quality: 'Jakość',
  },
  simulator: {
    overwrite: 'Nadpisz',
  },
  tableHeaders: {
    alarmType: 'Typ alarmu',
    avg: 'Średnia {unit}',
    description: 'Opis',
    max: 'Max {unit}',
    min: 'Min {unit}',
    name: 'Nazwa',
    value: 'wartość',
    valueWhenDeclared: 'Wartość przy wyzwoleniu',
    timeOfDeclaration: 'Czas wyzwolenia',
  },
  tabs: {
    materials: 'Materiały',
    materialsGroups: 'Grupy materiałów',
  },
  tiles: {
    userManagement: 'Zarządzanie użytkownikami',
    materials: 'Materiały',
    part: 'Części',
    loadingScheme: 'Schematy załadunku',
    productionLogs: 'Dzienniki produkcji',
    customer: 'Klient',
  },
  titles: {
    alarms: 'Alarmy',
    noData: 'Brak danych',
    settings: 'Ustawienia',
    valuesAndCalculations: 'Wartości i obliczenia',
  },
  type: {
    Visualisation,
  },
  upcPro: {
    analyzerReading: 'Odczyt analizatora',
    convertsTo: 'konwertuje na',
    endoGeneratorDataError: 'BŁĄD WPROWADZANIA DANYCH',
    floMeterGlossary: 'Słownik terminów',
    inputGroup: 'Wejście',
    flows: 'Przepływy wejściowe',
    furnaceAtmosphere: 'Atmosfera pieca',
    imperialUnits: 'imperialny',
    metricUnits: 'metryczny',
    potentials: 'Potencjały',
    inputs: {
      CELSIUS_TO_FAHRENHEIT: 'Celsjusza na Fahrenheita',
      FAHRENHEIT_TO_CELSIUS: 'Fahrenheita na Celsjusza',
      FT3PH_TO_M3PH: 'cfh na m³/h',
      GPM_TO_LMIN: 'US gpm na l/min',
      HARDNESS_TABLE1: 'Rockwell C Range',
      HARDNESS_TABLE2: 'Rockwell B Range',
      HBS: {
        name: 'HBS',
        subtitle: 'Skala Brinella',
      },
      HBS2: {
        name: 'HBS',
        subtitle: 'Skala Brinella',
      },
      HBW: {
        name: 'HBW',
        subtitle: 'Skala Brinella',
      },
      HK: {
        name: 'HK',
        subtitle: 'Twardość Knoopa',
      },
      HK2: {
        name: 'HK',
        subtitle: 'Twardość Knoopa',
      },
      HR15N: {
        name: 'HR15N',
        subtitle: 'Skala Rockwell 15-N',
      },
      HR15T: {
        name: 'HR15T',
        subtitle: 'Skala Rockwell 15-T',
      },
      HR30N: {
        name: 'HR30N',
        subtitle: 'Skala Rockwell 30-N',
      },
      HR30T: {
        name: 'HR30T',
        subtitle: 'Skala Rockwella 30-T',
      },
      HR45N: {
        name: 'HR45N',
        subtitle: 'Skala Rockwell 45-N',
      },
      HR45T: {
        name: 'HR45T',
        subtitle: 'Skala Rockwell 45-T',
      },
      HRA: {
        name: 'HRA',
        subtitle: 'Skala Rockwella A',
      },
      HRA2: {
        name: 'HRA',
        subtitle: 'Skala Rockwella A',
      },
      HRB: {
        name: 'HRB',
        subtitle: 'Twardość Rockwella B',
      },
      HRC: {
        name: 'HRC',
        subtitle: 'Twardość Rockwella C',
      },
      HRD: {
        name: 'HRD',
        subtitle: 'Skala Rockwella D',
      },
      HRF: {
        name: 'HRF',
        subtitle: 'Twardość Rockwella F',
      },
      HSC: {
        name: 'HSc',
        subtitle: 'Twardość skleroskopu HSc',
      },
      HV: {
        name: 'HV',
        subtitle: 'Twardość Vickersa',
      },
      HV2: {
        name: 'HV',
        subtitle: 'Twardość Vickersa',
      },
      LMIN_TO_GPM: 'l/min na galony amerykańskie na minutę',
      M3PH_TO_FT3PH: 'm³/h na cfh',
      ac: 'ac',
      actualScale: 'Rzeczywista skala',
      aisi: 'AISI',
      al: 'Glin',
      alloyFactor: 'Współczynnik stopu',
      baseC: 'Baza C',
      c: 'C',
      carbonPotential: 'Potencjał węglowy',
      cch4: 'C + CH₄',
      ccontent: 'Treść C',
      ch3oh: 'CH₃OH',
      ch4: 'CH₄',
      ch4Factor: 'Czynnik CH₄',
      co: 'CO',
      co2: 'CO₂',
      coTotalFlow: 'CO/Całkowity przepływ',
      coco2: 'CO/CO₂',
      coldJunctionTemp: 'Temperatura zimnego złącza.',
      cpatm: 'CPatm',
      cr: 'Kr',
      cu: 'Cu',
      dewPoint: 'Punkt rosy',
      din: 'DIN',
      dissBurette: 'Biureta rozszerzona',
      dissDegree: 'Stopień doktora',
      dnh3: 'D-NH₃',
      dpt: 'DPT',
      emf: 'PEM',
      floMeterScale: 'Skala Flo-Meter',
      flow: 'Przepływ',
      gas: 'Gaz',
      h2: 'H₂',
      h2n2: 'H₂',
      h2o: 'H₂O',
      hardness: 'Twardość',
      kcb: 'Kᴄʙ',
      kh: 'Kʜ',
      kn: 'Kɴ',
      ko: 'Kᴏ',
      mn: 'Mn',
      mo: 'Mo',
      mv: 'mV',
      n2: 'N₂',
      n2Percentage: 'N₂',
      n2ch3oh: 'N₂/CH₃OH',
      nc2: 'NC₂',
      nh3: 'NH₃',
      ni: 'Ni',
      o2: 'O₂',
      o2Percentage: 'O₂',
      o2Probe: 'Sonda O₂',
      others: 'Inni',
      p1: 'P1',
      p2: 'P2',
      po2: 'pO₂',
      preload: 'Wstępne ładowanie',
      pt10: {
        description: '(Pt10) Platynowy czujnik RTD o rezystancji nominalnej 10 omów w temperaturze 0°C (32°F).\nZakres',
        name: '(Pt10) Platyna RTD',
      },
      pt100: {
        description: '(Pt100) Platynowy czujnik RTD o rezystancji nominalnej 100 omów w temperaturze 0°C (32°F).\nZakres',
        name: '(Pt100) Platynowy czujnik RTD',
      },
      pt1000: {
        description: '(Pt1000) Platynowy czujnik RTD o rezystancji nominalnej 1000 omów w temperaturze 0°C (32°F).\nZakres',
        name: '(Pt1000) Platynowy czujnik RTD',
      },
      pt500: {
        description: '(Pt500) Platynowy czujnik RTD o rezystancji nominalnej 500 omów w temperaturze 0°C (32°F).\nZakres',
        name: '(Pt500) Platynowy czujnik RTD',
      },
      resistance: 'Opór',
      rg: 'RG',
      rl: 'RL',
      scaleFactor: 'Współczynnik skali',
      sg1: 'SG1',
      sg2: 'SG2',
      si: 'Si',
      sootLimit: 'Granica sadzy',
      tc_b: {
        description: 'Plus: Platyna 30% rodu (Pt30Rh)\nMinus: Platyna 6% rodu (Pt6Rh)\nZakres: -50°C–1820°C (-58°F–3308°F)\nKolory zgodne z normą ANSI 96.6.\nZgodnie z normą NFC 42-324, kolory to żółty i szary',
        name: 'Typ B = Pt30Rh-Pt6Rh',
      },
      tc_e: {
        description: 'Plus: Nikiel-Chrom (NiCr), znany również jako Chromel\nMinus: Miedź-Nikiel (CuNi), znany również jako Konstantan\nZakres: -270°C-1000°C (-454°F-1832°F)\\Kolory zgodne z normą DIN EN 60584-1',
        name: 'Typ E = NiCr-CuNi',
      },
      tc_j: {
        description: 'Plus: Miedź (Cu), Kolor miedzi\nMinus: Miedź-nikiel (CuNi), znany również jako Konstantan\nZakres: -270°C–400°C (-454°F–752°F)\\Kolory zgodne z normą DIN EN 60584-1',
        name: 'Typ J = Fe-CuNi',
      },
      tc_k: {
        description: 'Plus: Nikiel-chrom (NiCr), znany również jako Chromel\nMinus: Nikiel-aluminium (NiLe), znany również jako Alumel, drut magnetyczny\nZakres: -270°C-1372°C (-454°F-2502°F)\\Kolory zgodnie z normą DIN EN 60584-1',
        name: 'Typ K = NiCr-Ni',
      },
      tc_l: {
        description: 'Plus: Żelazo (Fe), drut magnetyczny\nMinus: Miedź-nikiel (CuNi), znany również jako Konstantan\nZakres temperatur: 0°C–900°C (32°F–1652°F)\\Kolory zgodnie z normą DIN EN 43714',
        name: 'Typ L = Fe-CuNi',
      },
      tc_n: {
        description: 'Plus:Nikiel-Chrom-Krzem (NiCrSi),\nznany również jako Nicrosil\nMinus:Nikiel-Krzem (NiSi),\nznany również jako Nisil\nZakres: -270°C-1300°C (-454°F-2372°F)\nKolory zgodne z normą DIN EN 60584-1',
        name: 'Typ N = NiCrSi-NiSi',
      },
      tc_r: {
        description: 'Plus: Platyna 13% rodu (Pt13Rh)\\Minus: Platyna (Pt), drut ujemny bardziej miękki\nniż drut dodatni\nZakres: -50°C–1768°C (-58°F–3214°F)\\Kolory zgodnie z normą DIN EN 60584-1',
        name: 'Typ R = Pt13Rh-Pt',
      },
      tc_s: {
        description: 'Plus: Platyna 10% rod (Pt 10 Rh)\\Minus: Platyna (Pt), drut ujemny bardziej miękki\nniż drut dodatni\nZakres: -50°C–1768°C (-58°F–3214°F)\\Kolory zgodnie z normą DIN EN 60584-1',
        name: 'Typ S = Pt10Rh-Pt',
      },
      tc_t: {
        description: 'Plus: Żelazo (Fe), drut magnetyczny\nMinus: Miedź-nikiel (CuNi), znany również jako Konstantan\nZakres: -210°C–1200°C (-346°F–2192°F)\\Kolory zgodnie z normą DIN EN 60584-1',
        name: 'Typ T = Cu-CuNi',
      },
      tc_u: {
        description: 'Plus: Miedź (Cu), Kolor miedzi\nMinus: Miedź-nikiel (CuNi), znany również jako Konstantan\nZakres: 0°C–600°C (32°F–1112°F)\\Kolory zgodne z normą DIN EN 43714',
        name: 'Typ U = Cu-CuNi',
      },
      temp1: 'Temp.1',
      temp2: 'Temp.2',
      temperature: 'Temperatura',
      total: 'Całkowity',
      totalFlow: 'Całkowity przepływ',
      v: 'V',
      voltage: 'Woltaż',
      weight1: 'Waga₁',
      weight2: 'Waga₂',
    },
    mustBeInteger: 'Musi być liczbą całkowitą',
    outOfRange: 'Poza zasięgiem',
    range: 'Zakres',
    units: 'Jednostki',
  },
  validation: {
    required: 'Wymagany',
    tooBigFile: 'Rozmiar pliku powinien być mniejszy niż 50 MB',
  },
  util: {
    schaefflerChart: {
      CrEquivalent: 'Ekwiwalent Cr',
      NiEquivalent: 'Ekwiwalent Ni',
    },
  },
  useImportJsonValidated: {
    unknownFileValidationError: 'Nie udało się zweryfikować przesłanego pliku. Dane nie są zgodne z oczekiwaniami',
  },
  tenantSettings: {
    dataSources: {
      headline: 'Źródła danych',
      subheadline: 'Na tej stronie zarządzane są źródła danych Qmulus.',
    },
    license: {
      headline: 'Klucz licencyjny',
      subheadline: 'Ta strona umożliwia przeglądanie i aktualizację klucza licencyjnego Qmulus.',
    },
    auditTrail: {
      headline: 'Dziennik zmian',
      subheadline: 'Dziennik zmian rejestruje każdą modyfikację dokonaną w aplikacji i przypisuje ją do odpowiedzialnego użytkownika, zapewniając pełną identyfikowalność wszystkich działań.',
    },
  },
  unitSystem,
  ...getComponentScopedTranslationMap('page', 'pl'),
  ...getComponentScopedTranslationMap('components', 'pl'),
  ...getComponentScopedTranslationMap('pt9800', 'pl'),
};
